import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useArticleCounts } from "@/features/search/hooks/useArticleCounts";
import { TrendingTag } from "@/features/trending/types/TrendingTags";
import DayTrendingPanel from "./DayTrendingPanel";

// Pixel constants for consistent calculations
const CELL_PX = 16; // Tailwind w-4 → 16px
const GAP_PX = 4; // Tailwind space-x-1 → 4px
const WEEK_PX = CELL_PX + GAP_PX; // 20px per week (column)

interface DayData {
  trendingTags: TrendingTag[];
  articleTitles: string[];
}

// Clean heat map colors with subtle borders for color-blind accessibility
const getHeatMapColor = (intensity: number) => {
  switch (intensity) {
    case 0:
      return "bg-gray-100 border-gray-200"; // No activity
    case 1:
      return "bg-green-100 border-gray-200"; // Low activity
    case 2:
      return "bg-green-200 border-gray-200"; // Medium-low activity
    case 3:
      return "bg-green-400 border-gray-200"; // Medium-high activity
    case 4:
      return "bg-green-600 border-gray-200"; // High activity
    case 5:
      return "bg-green-700 border-gray-200"; // Very high activity
    case 6:
      return "bg-green-800 border-gray-200"; // Very high activity
    case 7:
      return "bg-green-900 border-gray-200"; // Very high activity
    default:
      return "bg-gray-100 border-gray-200";
  }
};

// Generate calendar grid following GitHub's approach
const generateCalendarGrid = (year: number) => {
  const grid: (Date | null)[][] = [];

  // Start from January 1st of the target year
  const startDate = new Date(year, 0, 1);

  // Find the first Sunday of the year (GitHub starts weeks on Sunday)
  const firstSunday = new Date(startDate);
  while (firstSunday.getDay() !== 0) {
    firstSunday.setDate(firstSunday.getDate() - 1);
  }

  // Create 53 weeks (some years have 53 weeks)
  for (let week = 0; week < 53; week++) {
    const weekDays: (Date | null)[] = [];

    for (let day = 0; day < 7; day++) {
      const currentDate = new Date(firstSunday);
      currentDate.setDate(firstSunday.getDate() + week * 7 + day);

      // Only include dates within the target year
      if (currentDate.getFullYear() === year) {
        weekDays.push(new Date(currentDate));
      } else {
        weekDays.push(null);
      }
    }
    grid.push(weekDays);
  }

  return grid;
};

// Calculate month labels based on the calendar grid
const calculateMonthLabels = (grid: (Date | null)[][]) => {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const labels: { month: string; startWeek: number; endWeek: number }[] = [];

  let currentMonth = -1;
  let startWeek = 0;

  // Process each week column
  grid.forEach((week, weekIndex) => {
    // Find the first day of a month in this week
    let monthForWeek = -1;

    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const date = week[dayIndex];
      if (date && date.getDate() === 1) {
        monthForWeek = date.getMonth();
        break;
      }
    }

    // If no first day found, use the first valid date's month
    if (monthForWeek === -1) {
      const firstValidDate = week.find((date) => date !== null);
      if (firstValidDate) {
        monthForWeek = firstValidDate.getMonth();
      }
    }

    // If we found a new month, save the previous one and start tracking the new one
    if (monthForWeek !== -1 && monthForWeek !== currentMonth) {
      if (currentMonth !== -1) {
        labels.push({
          month: monthNames[currentMonth],
          startWeek: startWeek,
          endWeek: weekIndex - 1,
        });
      }
      currentMonth = monthForWeek;
      startWeek = weekIndex;
    }
  });

  // Add the last month
  if (currentMonth !== -1) {
    labels.push({
      month: monthNames[currentMonth],
      startWeek: startWeek,
      endWeek: grid.length - 1,
    });
  }

  return labels;
};

export default function NeoBrutalistHeatMap() {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayData, setSelectedDayData] = useState<DayData | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLoadingDayData, setIsLoadingDayData] = useState(false);
  const [selectedCliffNote, setSelectedCliffNote] =
    useState<TrendingTag | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  const {
    articleCounts,
    isLoading,
    loadArticleCounts,
    getIntensityForDate,
    getTrendingTagsForDate,
  } = useArticleCounts();

  // Generate calendar grid and month labels
  const calendarGrid = useMemo(
    () => generateCalendarGrid(currentYear),
    [currentYear]
  );
  const monthLabels = useMemo(
    () => calculateMonthLabels(calendarGrid),
    [calendarGrid]
  );

  // Local date key function to avoid UTC shift bugs
  const getLocalDateKey = useCallback((d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  const getDateKey = useCallback(
    (date: Date) => getLocalDateKey(date),
    [getLocalDateKey]
  );

  // Load article counts when year changes
  useEffect(() => {
    loadArticleCounts(currentYear);
  }, [currentYear, loadArticleCounts]);

  // Auto-scroll to the last 3 months with data when component loads
  useEffect(() => {
    if (calendarGrid.length > 0 && Object.keys(articleCounts).length > 0) {
      // Find the latest date with data
      let latestDate: Date | null = null;
      let latestDateKey = "";

      Object.keys(articleCounts).forEach((dateKey) => {
        if (articleCounts[dateKey] > 0) {
          const [year, month, day] = dateKey.split("-").map(Number);
          const date = new Date(year, month - 1, day);
          if (!latestDate || date > latestDate) {
            latestDate = date;
            latestDateKey = dateKey;
          }
        }
      });

      // If we found data, scroll to center the last 3 months
      if (latestDate) {
        setTimeout(() => {
          if (scrollContainerRef.current) {
            // Find latest date's position in grid
            let latestWeekIndex = -1;
            calendarGrid.forEach((week, weekIndex) => {
              week.forEach((date) => {
                if (date && getDateKey(date) === latestDateKey) {
                  latestWeekIndex = weekIndex;
                }
              });
            });

            if (latestWeekIndex !== -1) {
              // Show last 4 weeks
              const weeksToShow = 4;
              const middleWeekIndex = Math.max(
                0,
                latestWeekIndex - Math.floor(weeksToShow / 2)
              );

              const scrollPosition = middleWeekIndex * WEEK_PX;
              const centerOffset = 150;
              const finalScrollPosition = Math.max(
                0,
                scrollPosition - centerOffset
              );

              scrollContainerRef.current.scrollLeft = finalScrollPosition;
            }
          }
        }, 200);
      } else {
        // No data, scroll to today as fallback
        const today = new Date();
        const todayKey = getLocalDateKey(today);
        let todayWeekIndex = -1;
        calendarGrid.forEach((week, weekIndex) => {
          week.forEach((date) => {
            if (date && getDateKey(date) === todayKey) {
              todayWeekIndex = weekIndex;
            }
          });
        });
        if (todayWeekIndex !== -1) {
          setTimeout(() => {
            if (scrollContainerRef.current) {
              const scrollPosition = todayWeekIndex * WEEK_PX;
              const centerOffset = 150;
              const finalScrollPosition = Math.max(
                0,
                scrollPosition - centerOffset
              );
              scrollContainerRef.current.scrollLeft = finalScrollPosition;
            }
          }, 200);
        }
      }
    }
  }, [calendarGrid, articleCounts, getLocalDateKey, getDateKey]);

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Calculate total contributions for the year
  const totalContributions = useMemo(() => {
    return Object.values(articleCounts).reduce((sum, count) => sum + count, 0);
  }, [articleCounts]);

  const navigateYear = (direction: "prev" | "next") => {
    setCurrentYear((prev) => (direction === "prev" ? prev - 1 : prev + 1));
  };

  const handleDayClick = async (date: Date) => {
    try {
      setIsLoadingDayData(true);
      // Fetch the trending tags and articles for this date
      const dayData = await getTrendingTagsForDate(date);

      // Set the selected date and data
      setSelectedDate(date);
      setSelectedDayData(dayData);
      setIsPanelOpen(true);

      // Auto-select first trending tag if available
      if (dayData.trendingTags.length > 0) {
        setSelectedCliffNote(dayData.trendingTags[0]);
      }
    } catch (error) {
      console.error("Error handling day click:", error);
      // Still open the panel but with empty data
      setSelectedDate(date);
      setSelectedDayData({ trendingTags: [], articleTitles: [] });
      setIsPanelOpen(true);
      setSelectedCliffNote(null);
    } finally {
      setIsLoadingDayData(false);
    }
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
    setSelectedDate(null);
    setSelectedDayData(null);
    setSelectedCliffNote(null);
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-5xl mx-auto bg-white rounded-lg border border-gray-300 shadow-sm p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-lg font-bold text-black">
            Loading article data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Custom Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-lg pointer-events-none"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translateX(-50%)",
          }}
        >
          {tooltip.text}
        </div>
      )}

      <header className="text-center text-xl font-regular text-gray-600 mb-6">
        Click any day to get the latest in AI
      </header>

      {/* Calendar Heat Map */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-lg p-8 backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-medium text-black">
              {totalContributions} AI articles in {currentYear}
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => navigateYear("prev")}
                className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigateYear("next")}
                className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Heat map grid */}
        <div className="flex space-x-1">
          {/* Day labels */}
          <div className="flex flex-col space-y-1 mr-3">
            {weekDays.map((day, index) => (
              <div
                key={day}
                className="h-4 text-xs font-bold text-black text-center leading-4"
              >
                {index % 2 === 1 ? day : ""}
              </div>
            ))}
          </div>

          {/* Scrollable container with month labels and calendar grid */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-x-auto"
            style={{ minWidth: 0 }}
          >
            {/* Month labels */}
            <div
              className="relative flex mb-6 ml-8"
              style={{ width: `${calendarGrid.length * WEEK_PX}px` }}
            >
              {monthLabels.map((label, index) => {
                const weeks = label.endWeek - label.startWeek + 1;
                const width = weeks * WEEK_PX - GAP_PX; // subtract last gap
                const leftPosition = label.startWeek * WEEK_PX;

                return (
                  <div
                    key={`${label.month}-${index}`}
                    className="text-sm font-bold text-black text-center absolute"
                    style={{
                      left: `${leftPosition}px`,
                      width: `${width}px`,
                    }}
                  >
                    {label.month}
                  </div>
                );
              })}
            </div>

            {/* Calendar grid */}
            <div
              className="flex space-x-1"
              style={{ width: `${calendarGrid.length * WEEK_PX}px` }}
            >
              {calendarGrid.map((week, weekIndex) => (
                <div key={weekIndex} className="flex flex-col space-y-1">
                  {week.map((date, dayIndex) => {
                    if (!date) {
                      return <div key={dayIndex} className="w-4 h-4" />;
                    }

                    const intensity = getIntensityForDate(date);
                    const dateKey = getDateKey(date);
                    const isToday = dateKey === getLocalDateKey(new Date());
                    const isSelected =
                      selectedDate && dateKey === getDateKey(selectedDate);

                    // Get the actual article count for this date
                    const actualCount = articleCounts[getDateKey(date)] || 0;

                    // Format the date nicely
                    const monthName = date.toLocaleDateString("en-US", {
                      month: "long",
                    });
                    const dayNumber = date.getDate();
                    const year = date.getFullYear();

                    // Create tooltip text
                    const tooltipText =
                      actualCount === 0
                        ? `No articles on ${monthName} ${dayNumber}, ${year}`
                        : `${actualCount} article${
                            actualCount === 1 ? "" : "s"
                          } on ${monthName} ${dayNumber}, ${year}`;

                    return (
                      <button
                        key={dayIndex}
                        onClick={() => handleDayClick(date)}
                        className={`
                      w-4 h-4 rounded-sm border transition-all duration-200
                      ${getHeatMapColor(intensity)}
                      ${
                        isSelected
                          ? "border-blue-500 shadow-md ring-2 ring-blue-400"
                          : isToday
                          ? "border-green-500 shadow-sm ring-1 ring-green-300"
                          : "hover:border-gray-300 hover:shadow-sm"
                      }
                      hover:scale-105 cursor-pointer
                    `}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({
                            text: tooltipText,
                            x: rect.left + rect.width / 2,
                            y: rect.top - 10,
                          });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 flex items-center justify-between border-t border-gray-300 pt-4">
          <div className="text-center text-sm font-medium text-gray-600">
            AI articles summarized per day
          </div>

          <div className="flex items-center space-x-3 text-sm font-bold text-black">
            <span>Less</span>
            <div className="flex space-x-1">
              {[0, 1, 2, 3, 4].map((intensity) => (
                <div
                  key={intensity}
                  className={`w-4 h-4 rounded-sm border ${getHeatMapColor(
                    intensity
                  )}`}
                />
              ))}
            </div>
            <span>More</span>
          </div>
        </div>
      </div>
      {/* Day Trending Panel */}
      <div className="w-full mt-6 max-w-4xl mx-auto">
        <DayTrendingPanel
          isOpen={isPanelOpen}
          onClose={handleClosePanel}
          selectedDate={selectedDate}
          selectedDayData={selectedDayData}
          isLoadingDayData={isLoadingDayData}
          selectedCliffNote={selectedCliffNote}
          onCliffNoteChange={setSelectedCliffNote}
        />
      </div>
    </div>
  );
}
